import L from 'leaflet'

// Default tile buffer size (in tiles)
const DEFAULT_TILEBUFFER = 5

/**
 * TileManager - Manages basemap and tile overlay layers
 *
 * Handles rendering of base maps and tile overlays (roads, railways, etc.)
 * using either standard Leaflet tile layers or custom proxy tile loading.
 */
export default class TileManager {
  /**
   * Create a new TileManager
   * @param {L.Map} leafletMap - The Leaflet map instance
   * @param {Object} [options={}] - Configuration options
   * @param {number} [options.maxzoom=19] - Maximum zoom level
   * @param {number} [options.tilebuffer=5] - Tile buffer size for edge buffering
   * @param {Function} [options.warnFn] - Optional warning function for logging
   */
  constructor(leafletMap, options = {}) {
    this._map = leafletMap
    this._maxzoom = options.maxzoom || 19
    this._tilebuffer = options.tilebuffer || DEFAULT_TILEBUFFER
    this._warn = options.warnFn || console.warn

    this._tileOverlays = {}
    this._currentBasemapLayer = null
    this._basemap = null
    this._tileloader = null
  }

  /**
   * Set the tile loader function
   * @param {Function} loaderFn - Async function(repo, x, y, z) => dataUrl
   */
  setTileLoader(loaderFn) {
    this._tileloader = loaderFn
  }

  /**
   * Set the basemap source
   * @param {string} basemap - URL template or repo name (if tileloader set)
   */
  setBasemap(basemap) {
    this._basemap = basemap
    this._renderBasemap()
  }

  /**
   * Get current basemap
   * @returns {string} Current basemap
   */
  getBasemap() {
    return this._basemap
  }

  /**
   * Set maximum zoom level
   * @param {number} zoom - Maximum zoom level
   */
  setMaxZoom(zoom) {
    this._maxzoom = zoom
  }

  /**
   * Render or update the base map layer
   *
   * Creates the tile layer for the base map using either:
   * - Custom tile loader (if tileloader function is provided) - for msysProxy pattern
   * - Standard Leaflet tile layer (if basemap is a URL template)
   *
   * @private
   */
  _renderBasemap() {
    if (this._currentBasemapLayer) {
      this._map.removeLayer(this._currentBasemapLayer)
    }

    if (!this._basemap) {
      this._warn('Basemap is not set, cannot render map')
      return
    }

    // If tileloader exists, treat basemap as repo name, otherwise as URL
    if (this._tileloader && typeof this._tileloader === 'function') {
      this._currentBasemapLayer = this._createProxyTileLayer(this._basemap, 0)
    } else {
      // Standard tile layer with URL
      this._currentBasemapLayer = L.tileLayer(this._basemap, {
        attribution: '',
        maxZoom: this._maxzoom,
        zIndex: 0,
      })
    }

    if (this._currentBasemapLayer) {
      this._currentBasemapLayer.addTo(this._map)
    }
  }

  /**
   * Reload/refresh the basemap and all tile overlays
   *
   * Useful when server connection is re-established or when tile sources change.
   */
  reload() {
    if (!this._map) return
    this._renderBasemap()

    // Also reload all tile overlays
    Object.values(this._tileOverlays).forEach(overlay => {
      if (overlay.layer) {
        this._map.removeLayer(overlay.layer)
        overlay.layer.addTo(this._map)
      }
    })
  }

  /**
   * Create a custom tile layer using the msysProxy tileloader function
   *
   * @param {string} repoName - Repository/map name for tile loader
   * @param {number} [zIndex=0] - Layer z-index for stacking order
   * @returns {L.GridLayer} Leaflet GridLayer instance with custom tile loading
   * @private
   */
  _createProxyTileLayer(repoName, zIndex = 0) {
    if (!repoName || typeof this._tileloader !== 'function') {
      return null
    }
    const self = this

    const ProxyGridLayer = L.GridLayer.extend({
      options: {
        edgeBufferTiles: self._tilebuffer,
      },
      createTile: function (coords, done) {
        const tile = document.createElement('img')

        const size = this.getTileSize()
        tile.width = size.x
        tile.height = size.y
        tile.alt = ''

        tile.onload = () => {
          done(null, tile)
        }

        tile.onerror = (e) => {
          done(e || new Error('tile load error'), tile)
        }

        // Load via async tileloader -> data URL
        self._tileloader(repoName, coords.x, coords.y, coords.z)
          .then((dataUrl) => {
            if (tile.isConnected) {
              tile.src = dataUrl
            }
          })
          .catch((err) => {
            done(err, tile)
          })

        return tile
      },

      getAttribution: function () {
        return ''
      },

      // Clean up tile to release memory
      _removeTile: function (key) {
        const tile = this._tiles[key]
        if (tile && tile.el) {
          tile.el.onload = null
          tile.el.onerror = null
          tile.el.src = ''
        }
        L.GridLayer.prototype._removeTile.call(this, key)
      },
    })

    return new ProxyGridLayer({ zIndex })
  }

  /**
   * Add or update a tile overlay layer
   *
   * @param {string} id - Overlay identifier (e.g., 'roads', 'railways')
   * @param {Object} [cfg={}] - Configuration options
   * @param {string} cfg.url - URL template or repo name (if tileloader set)
   * @param {number} [cfg.zindex=400] - Layer z-index for stacking order
   */
  setTileOverlay(id, cfg = {}) {
    // Remove existing overlay if updating
    if (this._tileOverlays[id]) {
      this.removeTileOverlay(id)
    }
    const url = cfg.url
    const zIndex = cfg.zindex || 400

    let tileLayer

    // If tileloader exists, treat config as repo name, otherwise as URL
    if (this._tileloader && typeof this._tileloader === 'function') {
      tileLayer = this._createProxyTileLayer(url, zIndex)
    } else {
      tileLayer = L.tileLayer(url, {
        attribution: '',
        maxZoom: this._maxzoom,
        zIndex,
        edgeBufferTiles: this._tilebuffer,
      })
    }

    // Store overlay configuration
    this._tileOverlays[id] = {
      layer: tileLayer,
      url,
      zindex: zIndex,
    }

    // Add to map
    if (tileLayer) {
      tileLayer.addTo(this._map)
    }
  }

  /**
   * Remove a tile overlay layer
   *
   * @param {string} id - Overlay identifier to remove
   */
  removeTileOverlay(id) {
    const overlay = this._tileOverlays[id]
    if (!overlay) return

    this._map.removeLayer(overlay.layer)
    delete this._tileOverlays[id]
  }

  /**
   * Get all currently configured tile overlays
   *
   * @returns {Array<Object>} Array of overlay configurations with id, url, and zindex
   */
  getTileOverlays() {
    return Object.keys(this._tileOverlays).map(id => ({
      id,
      url: this._tileOverlays[id].url,
      zindex: this._tileOverlays[id].zindex,
    }))
  }

  /**
   * Clean up all tile layers
   */
  destroy() {
    if (this._currentBasemapLayer) {
      this._map.removeLayer(this._currentBasemapLayer)
      this._currentBasemapLayer = null
    }

    Object.keys(this._tileOverlays).forEach(id => {
      this.removeTileOverlay(id)
    })
  }
}
